import React, { useState } from 'react';
import OTPAuth from './OTPAuth';
import ForgotPassword from './ForgotPassword';

interface AuthScreenProps {
  onLogin: (token: string, user: any) => void;
}

const AuthScreen: React.FC<AuthScreenProps> = ({ onLogin }) => {
  const [useOTP, setUseOTP] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    address: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // If Forgot Password mode selected
  if (showForgotPassword) {
    return (
      <ForgotPassword
        onBack={() => setShowForgotPassword(false)}
        onSuccess={() => {
          setShowForgotPassword(false);
          setIsLogin(true);
        }}
      />
    );
  }

  // If OTP mode selected, show OTP component
  if (useOTP) {
    return (
      <div>
        <OTPAuth onSuccess={onLogin} />
        <div style={{
          textAlign: 'center',
          marginTop: '20px'
        }}>
          <button
            onClick={() => setUseOTP(false)}
            style={{
              background: 'transparent',
              color: '#6366f1',
              border: '2px solid #6366f1',
              padding: '10px 20px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            ← Use Email/Password Instead
          </button>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const endpoint = isLogin ? '/users/login' : '/users/register';
      const payload = isLogin ? {
        email: formData.email.trim().toLowerCase(),
        password: formData.password
      } : formData;

      console.log('Submitting to:', endpoint);
      console.log('Email:', payload.email || formData.email);

      const response = await fetch(`https://api.bringit.co.in/api${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      console.log('Response:', response.status, data);

      if (response.ok) {
        localStorage.setItem('token', data.token);
        onLogin(data.token, data.user || data);
      } else {
        setError(data.message || 'Authentication failed');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Logo */}
        <div style={styles.logoSection}>
          <img src="/logo-new.png" alt="BringIt" style={styles.logoImage} />
          <h1 style={styles.title}>BringIt</h1>
          <p style={styles.subtitle}>Grocery, Food & More Delivered Fast!</p>
        </div>

        {/* Tabs */}
        <div style={styles.tabs}>
          <button
            onClick={() => setIsLogin(true)}
            style={{...styles.tab, ...(isLogin ? styles.activeTab : {})}}
          >
            Login
          </button>
          <button
            onClick={() => setIsLogin(false)}
            style={{...styles.tab, ...(!isLogin ? styles.activeTab : {})}}
          >
            Register
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={styles.form}>
          {!isLogin && (
            <input
              type="text"
              name="name"
              placeholder="Full Name"
              value={formData.name}
              onChange={handleChange}
              required={!isLogin}
              style={styles.input}
            />
          )}

          <input
            type="email"
            name="email"
            placeholder="Email Address"
            value={formData.email}
            onChange={handleChange}
            required
            style={styles.input}
          />

          {!isLogin && (
            <input
              type="tel"
              name="phone"
              placeholder="Phone Number (10 digits)"
              value={formData.phone}
              onChange={handleChange}
              required={!isLogin}
              pattern="[0-9]{10}"
              style={styles.input}
            />
          )}

          <input
            type="password"
            name="password"
            placeholder="Password (min 6 characters)"
            value={formData.password}
            onChange={handleChange}
            required
            minLength={6}
            style={styles.input}
          />

          {!isLogin && (
            <input
              type="text"
              name="address"
              placeholder="Delivery Address"
              value={formData.address}
              onChange={handleChange}
              required={!isLogin}
              style={styles.input}
            />
          )}

          {error && (
            <div style={styles.error}>
              {error}
              {isLogin && error.includes('Invalid email or password') && (
                <div style={{ marginTop: '10px', fontSize: '13px' }}>
                  💡 <strong>Tips:</strong>
                  <ul style={{ textAlign: 'left', margin: '8px 0', paddingLeft: '20px' }}>
                    <li>Make sure email and password are correct</li>
                    <li>Check for extra spaces in email/password</li>
                    <li>Don't have an account? Click "Register" tab above</li>
                  </ul>
                </div>
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{...styles.button, ...(loading ? styles.buttonDisabled : {})}}
          >
            {loading ? 'Please wait...' : (isLogin ? 'Login' : 'Register')}
          </button>

          {/* Forgot Password Link */}
          {isLogin && (
            <div style={{ textAlign: 'center', marginTop: '10px' }}>
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#6366f1',
                  fontSize: '14px',
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
              >
                🔐 Forgot Password?
              </button>
            </div>
          )}
        </form>

        {/* OTP Login Option */}
        <div style={{
          marginTop: '20px',
          textAlign: 'center',
          padding: '15px',
          background: '#f5f5f5',
          borderRadius: '10px'
        }}>
          <p style={{ color: '#666', fontSize: '14px', marginBottom: '10px' }}>
            Or login with phone number
          </p>
          <button
            onClick={() => setUseOTP(true)}
            style={{
              background: '#6366f1',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              fontSize: '16px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            📱 Login with OTP
          </button>
          <p style={{ fontSize: '11px', color: '#999', marginTop: '8px' }}>
            Requires Twilio setup (see PHONE_OTP_SETUP.md)
          </p>
        </div>

        {/* Demo Credentials */}
        {isLogin && (
          <div style={styles.demoInfo}>
            <p style={styles.demoTitle}>📝 Demo Account:</p>
            <p style={styles.demoText}>Email: test@thunderbag.com</p>
            <p style={styles.demoText}>Password: test123</p>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #0a1628 0%, #1e3a5f 50%, #2d5a87 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
  },
  card: {
    background: 'rgba(255, 255, 255, 0.98)',
    borderRadius: '30px',
    padding: '50px 40px',
    maxWidth: '420px',
    width: '100%',
    boxShadow: '0 25px 80px rgba(0, 0, 0, 0.4), 0 0 100px rgba(99, 102, 241, 0.2)',
    backdropFilter: 'blur(10px)',
  },
  logoSection: {
    textAlign: 'center' as const,
    marginBottom: '30px',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: '15px',
  },
  logo: {
    fontSize: '60px',
    marginBottom: '10px',
  },
  logoImage: {
    width: '100px',
    height: '100px',
    objectFit: 'contain' as const,
    filter: 'drop-shadow(0 10px 25px rgba(99, 102, 241, 0.3))',
    background: 'white',
    borderRadius: '20px',
    padding: '15px',
    boxShadow: '0 8px 30px rgba(99, 102, 241, 0.4)',
    order: 1,
  },
  title: {
    fontSize: '36px',
    fontWeight: 'bold',
    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    margin: '0',
    order: 2,
  },
  subtitle: {
    color: '#64748b',
    fontSize: '14px',
    margin: '5px 0',
    fontWeight: '500',
  },
  tabs: {
    display: 'flex',
    marginBottom: '30px',
    gap: '12px',
    background: '#f1f5f9',
    padding: '6px',
    borderRadius: '16px',
  },
  tab: {
    flex: 1,
    padding: '14px',
    border: 'none',
    background: 'transparent',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    color: '#64748b',
  },
  activeTab: {
    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    color: 'white',
    boxShadow: '0 8px 20px rgba(99, 102, 241, 0.3)',
    transform: 'scale(1.02)',
  },
  form: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '18px',
  },
  input: {
    padding: '16px 20px',
    border: '2px solid #e2e8f0',
    borderRadius: '16px',
    fontSize: '16px',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    outline: 'none',
    background: '#f8fafc',
    fontFamily: 'inherit',
  },
  button: {
    padding: '16px',
    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '16px',
    fontSize: '18px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    marginTop: '10px',
    boxShadow: '0 10px 25px rgba(99, 102, 241, 0.3)',
  },
  buttonDisabled: {
    background: '#cbd5e1',
    cursor: 'not-allowed',
    boxShadow: 'none',
  },
  error: {
    background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
    color: '#dc2626',
    padding: '14px',
    borderRadius: '16px',
    fontSize: '14px',
    textAlign: 'center' as const,
    fontWeight: '600',
    border: '2px solid #fca5a5',
  },
  demoInfo: {
    marginTop: '25px',
    padding: '20px',
    background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
    borderRadius: '20px',
    textAlign: 'center' as const,
    border: '2px solid #bae6fd',
  },
  demoTitle: {
    fontWeight: 'bold',
    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    marginBottom: '8px',
  },
  demoText: {
    color: '#475569',
    fontSize: '14px',
    margin: '4px 0',
    fontWeight: '500',
  },
};

export default AuthScreen;
